import { useState, useCallback } from "react";
import { flushCITrainingJob } from "@/utils/api/datasets";
import { useStore } from "@/store";
import { CIEvaluationResult } from "@functions/continuousImprovement/continuousImprovementTypes";

interface FlushOptions {
    force?: boolean;
}

/**
 * Shared hook for flushing a CI training job (bypassing the debounce grace period).
 * Used by IntegrateSideMenu ("Train Now" card) and ImproveSidebarContent ("Approve & Retrain").
 *
 * Pass `force: true` to dispatch training even when no job is currently scheduled
 * (e.g. when the user crosses a threshold before the CI trigger fires).
 *
 * Sets an optimistic FLUSHING_TRAIN state in the store so the UI immediately shows
 * the "Retraining…" spinner without waiting for the Firestore round-trip (~500ms).
 * The optimistic state is cleared automatically when the Firestore listener receives
 * the real FLUSHING_TRAIN / TRAINING_IN_PROGRESS update from the backend.
 */
export function useFlushCITrainingJob(datasetId: string | null, options?: FlushOptions) {
    const [isFlushing, setIsFlushing] = useState(false);
    const setOptimisticCIResult = useStore((s) => s.setOptimisticCIResult);

    const flush = useCallback(async () => {
        if (!datasetId || isFlushing) return;
        setIsFlushing(true);
        setOptimisticCIResult(CIEvaluationResult.FLUSHING_TRAIN);
        try {
            await flushCITrainingJob(datasetId, { force: options?.force });
        } catch (error) {
            console.error("[useFlushCITrainingJob] Failed to flush CI training job:", error);
            setOptimisticCIResult(null);
        } finally {
            setIsFlushing(false);
        }
    }, [datasetId, isFlushing, options?.force, setOptimisticCIResult]);

    return { flush, isFlushing };
}
